import { sortBillingTypes } from './billing.js'
// โปรโมชั่น helpers — shared by Manager › ตั้งค่าโปรโมชั่น (list + drawer).
// Backend: Xano `promotion` table, read through /api/promotion-manage (the full catalog,
// including retired rows) and written through POST /api/promotion-manage +
// PATCH /api/promotion-manage/:id. The counter's picker reads a DIFFERENT endpoint
// (/api/promotions → the Default group's cached, active-only, five-column projection).
//
// The catalog is ~224 rows, so it is fetched ONCE and filtered in memory — same call as
// lib/dentist.js, and for the same reason: Xano offers no search or tri-state active
// filter on this table at all.
//
// ── What a `pattern` is ────────────────────────────────────────────────────────────────
// A promotion is not a price, it is a SCHEDULE of charges. `pattern[]` holds the steps in
// charging order, and `promotion_patient/add` picks which one to bill next with
//
//     ($promotion.pattern|sort:"repeat_acc")[$$.repeat_acc > (billing rows so far)]|first
//
// so `repeat_acc` is a RUNNING TOTAL of `repeat`, not a label. Step 1 ×1 then step 2 ×99
// gives repeat_acc 1 then 100: the first visit bills step 1, visits 2–100 bill step 2, and
// visit 101 bills nothing. `max` (the final repeat_acc) is the denominator printed on the
// receipt line — "จัดฟัน 999/1500 3/104".
//
// Both are DERIVED from the order of the array, here for live preview and again in Xano
// (function promotion_pattern_norm) which is the copy that decides what is stored.

export { activeInfo } from './employee.js'

/* ── pattern math ─────────────────────────────────────────────────────────── */

export const patternRows = (p) => (Array.isArray(p?.pattern) ? p.pattern : [])

// A blank step. `fixedDF: null` = "pay the doctor their percentage", which is the sane
// default: a new step should not silently pay 0.
export const emptyStep = () => ({ repeat: 1, billing_type_id: 0, price: 0, fixedDF: null })

/**
 * Read a STORED pattern into editable rows, in TRUE CHARGING ORDER, taking each step's
 * count from the GAP in `repeat_acc` rather than from the stored `repeat`.
 *
 * Both departures from the obvious reading are forced by the live data (audited over all
 * 224 promotions, 2026-08-12):
 *
 * 1. **Sort by repeat_acc, don't trust array order.** `promotion_patient/add` selects the
 *    step to bill with `($promotion.pattern|sort:"repeat_acc")[…]|first`, so repeat_acc
 *    order IS charging order. #201 "แพคเสริม สบายใจ" is stored as steps 1, 10, 11, 2, 3 … 9
 *    — rendering it in array order would show a jumbled schedule and, worse, compute the
 *    gaps off a sequence that goes 2, 11, 12, 3, 4 …
 *
 * 2. **Count = the gap in repeat_acc, not the stored `repeat`.** 21 promotions store a
 *    `repeat` that does not accumulate into their `repeat_acc`. Invisalign Lite (#105) is
 *    the clearest: repeat 1,1,1,2,1 against repeat_acc 2,4,6,14,15. Since nothing reads
 *    `repeat`, it is decorative and wrong on those rows — re-deriving repeat_acc from it
 *    would have cut that promotion from 15 installments to 6, a real change to what
 *    patients get charged.
 *
 * The round trip (gap → re-accumulate) reproduces the stored repeat_acc exactly for 221 of
 * 224. The three exceptions are already-broken rows: #191 has a lone step at repeat_acc 0,
 * which `repeat_acc > billings-so-far` can never select, so that promotion cannot bill at
 * all today; #129 and #201 have non-increasing sequences. All three fall back to a count of
 * 1 for the offending step, which is the repair — and `maxMismatch()` flags them in the UI
 * rather than letting a save change them silently.
 */
export function readPattern(p) {
  const sorted = [...patternRows(p)].sort(
    (a, b) => (Number(a?.repeat_acc) || 0) - (Number(b?.repeat_acc) || 0),
  )
  let prev = 0
  return sorted.map((r) => {
    const acc = Number(r?.repeat_acc)
    const gap = Number.isFinite(acc) ? acc - prev : NaN
    if (Number.isFinite(acc) && acc > prev) prev = acc
    const repeat = gap > 0 ? gap : (Math.trunc(Number(r?.repeat)) > 0 ? Math.trunc(Number(r.repeat)) : 1)
    const df = r?.fixedDF
    const dfNum = df === null || df === undefined || df === '' ? null : Number(df)
    return {
      repeat,
      billing_type_id: Math.trunc(Number(r?.billing_type_id)) || 0,
      price: Number(r?.price) || 0,
      fixedDF: dfNum !== null && Number.isFinite(dfNum) ? dfNum : null,
    }
  })
}

/**
 * Renumber `step` and re-accumulate `repeat_acc` down the array, exactly as Xano's
 * promotion_pattern_norm does. Call it after every add / remove / reorder so the drawer
 * shows the numbers that will actually be stored.
 */
export function normalizePattern(rows) {
  let acc = 0
  return (Array.isArray(rows) ? rows : []).map((r, i) => {
    const repeat = Math.trunc(Number(r?.repeat)) > 0 ? Math.trunc(Number(r.repeat)) : 1
    acc += repeat
    const df = r?.fixedDF
    const dfNum = df === null || df === undefined || df === '' ? null : Number(df)
    return {
      step: i + 1,
      repeat,
      repeat_acc: acc,
      billing_type_id: Math.trunc(Number(r?.billing_type_id)) || 0,
      price: Number(r?.price) || 0,
      fixedDF: dfNum !== null && Number.isFinite(dfNum) ? dfNum : null,
    }
  })
}

// Total billings the promotion can ever produce = the last step's repeat_acc.
export const patternMax = (rows) => {
  const n = normalizePattern(rows)
  return n.length ? n[n.length - 1].repeat_acc : 0
}

// Same number, read straight off a STORED promotion: the HIGHEST repeat_acc, which is where
// the debug POST /promotion_max backfill took it from and what the receipt denominator is
// meant to be. Highest, not last-in-array — #201 is stored out of order and its last array
// element is not its final step.
export const storedMax = (p) => patternRows(p)
  .reduce((m, r) => Math.max(m, Number(r?.repeat_acc) || 0), 0)

// What the promotion is worth end to end: each step's price × how many times it fires,
// with a step left at 0 counting as ฿0.
//
// Since 2026-09-01 that is also WHAT THE PATIENT PAYS. It used not to be: at billing time
// a step stored at 0 fell back to its billing_type's own price, so this total — and the
// step rows a manager reads above it — could sit below the real charge, which is exactly
// how #231 came to print ฿12,000 for a package that billed ฿17,000. The promotion now
// dictates the price. See knowledge/xano/promotion-price-zero.md.
export function patternTotal(rows) {
  return normalizePattern(rows).reduce((sum, r) => sum + r.price * r.repeat, 0)
}

// The stored `max` should equal the pattern's own highest repeat_acc — both as written,
// not re-derived, since that is the pair the receipt denominator is printed from.
//
// Re-audited 2026-08-12 across all 224 promotions: **three DISAGREE**, so this is a live
// check, not a formality —
//   #201 แพคเสริม สบายใจ       max 10 · pattern 12   (billing lines say 12)
//   #129 รากเทียม 29,000 (แบ่ง 2)  max  1 · pattern  2   (billing lines say 2)
//   #191 Home Retainer Duo      max  1 · pattern  1, but its only step sits at
//        repeat_acc 0, so `repeat_acc > billings-so-far` can never select it and the
//        promotion cannot bill at all. Flagged to Ram; needs fixing in Xano.
// (An earlier version of this comment claimed all 224 agreed. It did not.)
export function maxMismatch(p) {
  const stored = Number(p?.max)
  const computed = storedMax(p)
  if (!Number.isFinite(stored)) return null
  return stored === computed ? null : { stored, computed }
}

/* ── doctor fee (DF) ──────────────────────────────────────────────────────── */
//
// percentDoctorCal decides a step's DF in this order:
//   1. pattern.fixedDF is set        → the doctor gets EXACTLY that, 0 included
//   2. billing_type.percentDoctor    → that percentage of the price
//   3. Ortho type + doctor flagged ortho_special → 78%
//   4. three named doctors           → 53% (48% at Bangkok Central Westville)
//   5. everyone else                 → 48%
//
// So `null` and `0` are different instructions, and the editor must never collapse one
// into the other: null means "work it out per doctor", 0 means "this step pays nothing".

export const DF_FIXED = 'fixed'
export const DF_PERCENT = 'percent'

export const dfMode = (row) => (row?.fixedDF === null || row?.fixedDF === undefined ? DF_PERCENT : DF_FIXED)

// What rule 2–5 would land on for a step, ignoring the doctor (who is not known here).
// Returns a number when the billing type pins it, otherwise null for "depends on the doctor".
export function dfPercentHint(row, btById) {
  const bt = btById?.get?.(row?.billing_type_id)
  const pct = bt?.percentDoctor
  return pct === null || pct === undefined || pct === '' ? null : Number(pct)
}

/* ── billing types ────────────────────────────────────────────────────────── */

export const btLabel = (bt) => {
  if (!bt) return ''
  return bt.name || bt.nameEng || bt.receipt_type || `#${bt.id}`
}

// id → row, for the step editor and every price fallback.
export const indexBillingTypes = (list) => new Map((list || []).map((b) => [Number(b.id), b]))

// Options for the step's ประเภทบิล picker — one dropdown over the whole catalogue, but a
// GROUPED one: `type` is the workflow bucket ("Ortho" / "สปสช" / "Dent Pro", 37 of them) and
// it becomes a section header in the list, so 343 rows scan as a handful of blocks instead
// of one wall. SearchableSelect matches `group` in the search box, so typing a bucket name
// narrows to that block.
//
// Deliberately UNFILTERED on `active` — unlike every other billing-type picker in the app
// this one lists retired types next to live ones. A promotion is a schedule that outlives
// the catalogue: a legacy pattern may point at a type since retired, and a package may
// legitimately be built out of one that is retired for walk-in billing.
//
// `label` is the bare name because the header above it already carries the bucket, but
// `selectedLabel` puts the bucket back for the closed trigger — names collide across
// buckets ("ขูดหินปูน" exists under สปสช and elsewhere) and a shut dropdown has no header
// to disambiguate it.
//
// `receipt_type` is the receipt category ("ทันตกรรมเด็ก" / "สินค้าทางทันตกรรม") and rides on
// the hint line with the price; the English equivalents ride along in `search`, which
// SearchableSelect matches but never renders.
export function billingTypeOptions(list) {
  const btType = (b) => String(b?.type || '').trim()
  // Bucket, then live-before-retired, then name — the list renders in this order and
  // headers break on it, so it has to be grouped, not merely sorted. 142 of the 343
  // types are retired, so interleaving them by name would leave every block half dead
  // weight; they stay reachable, just below the live ones. The comparator moved to
  // lib/billing.js#sortBillingTypes when the ledger's picker needed the same thing.
  return sortBillingTypes(list)
    .map((b) => {
      const name = b.active === false ? `${btLabel(b)} (ปิดใช้งาน)` : btLabel(b)
      return {
        value: Number(b.id),
        group: btType(b) || 'ไม่ระบุประเภท',
        label: name,
        selectedLabel: btType(b) ? `${btType(b)} · ${name}` : name,
        hint: [b.receipt_type, b.price ? `฿${Number(b.price).toLocaleString()}` : '']
          .filter(Boolean).join(' · '),
        search: [b.nameEng, b.receipt_typeEN, b.StatType, b.advancePay].filter(Boolean).join(' '),
      }
    })
}

/* ── list filtering ───────────────────────────────────────────────────────── */

export function normText(s) {
  return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim()
}

const haystack = (p) => normText([p?.Name, p?.Maintype, p?.Subtype, p?.Detail].filter(Boolean).join(' '))

// Options with live counts for the ประเภท / หมวดย่อย dropdowns. Derived from the data:
// Maintype and Subtype are free text, so a category added in Xano appears here with no
// code change. Blank values get a real "ไม่ระบุ" bucket rather than vanishing.
export const NO_TYPE = '__none__'

export function typeOptions(rows, key) {
  const count = new Map()
  for (const p of rows || []) {
    const v = String(p?.[key] || '').trim() || NO_TYPE
    count.set(v, (count.get(v) || 0) + 1)
  }
  return [...count.entries()]
    .map(([value, n]) => ({ value, label: value === NO_TYPE ? 'ไม่ระบุ' : value, count: n }))
    .sort((a, b) => {
      if (a.value === NO_TYPE) return 1
      if (b.value === NO_TYPE) return -1
      return a.label.localeCompare(b.label, 'th')
    })
}

/**
 * The ประเภท / หมวดย่อย values already in use, for the editor's creatable comboboxes.
 * Both columns are free text with no enum behind them, so the only catalog that exists is
 * the one the data itself makes — which is exactly why the picker must also accept a new
 * value rather than trapping the user in what has been typed before.
 *
 * `subsByMain` lets the หมวดย่อย list narrow to the chosen ประเภท: "Metal" belongs under
 * จัดฟัน and offering it under Dental Spa is noise. It falls back to every subtype when the
 * chosen ประเภท is new or has none yet, so the list is never empty when values exist.
 */
export function typeCatalog(rows) {
  const mains = new Set()
  const subs = new Set()
  const subsByMain = new Map()
  for (const p of rows || []) {
    const m = String(p?.Maintype || '').trim()
    const s = String(p?.Subtype || '').trim()
    if (m) mains.add(m)
    if (s) subs.add(s)
    if (m && s) {
      if (!subsByMain.has(m)) subsByMain.set(m, new Set())
      subsByMain.get(m).add(s)
    }
  }
  const th = (a, b) => a.localeCompare(b, 'th')
  return {
    mains: [...mains].sort(th),
    subs: [...subs].sort(th),
    subsFor: (main) => {
      const set = subsByMain.get(String(main || '').trim())
      return set?.size ? [...set].sort(th) : [...subs].sort(th)
    },
  }
}

/**
 * @param rows    the whole catalog
 * @param q       free text over name / type / subtype / detail
 * @param mains   Set of Maintype values (OR), empty = any
 * @param subs    Set of Subtype values (OR), empty = any
 * @param active  'true' | 'false' | '' (any) — same tri-state string as the dentist list
 */
export function filterPromotions(rows, { q = '', mains, subs, active = 'true' } = {}) {
  const needle = normText(q)
  return (rows || []).filter((p) => {
    if (active === 'true' && p.active !== true) return false
    if (active === 'false' && p.active === true) return false
    if (mains?.size && !mains.has(String(p.Maintype || '').trim() || NO_TYPE)) return false
    if (subs?.size && !subs.has(String(p.Subtype || '').trim() || NO_TYPE)) return false
    if (needle && !haystack(p).includes(needle)) return false
    return true
  })
}

// ประเภท → หมวดย่อย → ชื่อ, so the table reads like the catalog it is.
export function byCatalogOrder(a, b) {
  const m = String(a.Maintype || '').localeCompare(String(b.Maintype || ''), 'th')
  if (m) return m
  const s = String(a.Subtype || '').localeCompare(String(b.Subtype || ''), 'th')
  if (s) return s
  return String(a.Name || '').localeCompare(String(b.Name || ''), 'th')
}

/* ── formatting ───────────────────────────────────────────────────────────── */

export const baht = (n) => {
  const v = Number(n)
  if (!Number.isFinite(v)) return '—'
  return v.toLocaleString('th-TH', { maximumFractionDigits: 2 })
}

// "×99" reads better than "99 ครั้ง" in a dense step row, but a single-fire step needs no
// multiplier at all.
export const repeatLabel = (n) => (Number(n) > 1 ? `×${Number(n).toLocaleString()}` : '×1')
